import type { ReactNode } from 'react';
import SelectField from '../form/SelectField';
import type { LabelPlacement } from '../form/labelPlacement';

/* ------------------------------------------------------------------ */
/*  AppSelectBox                                                      */
/* ------------------------------------------------------------------ */

/**
 * The app's dropdown. Renders the shared MUI `SelectField` (components/form).
 *
 * Behaviour (see SelectField for the detail):
 * - Typing filters (contains) and underlines the match; it never picks or
 *   highlights anything for you. The value changes only on a click, Enter on
 *   an option reached with the arrow keys or mouse, or the clear button.
 * - `onValueChanged` fires ONLY for those user actions. Setting `value` from
 *   code (loading a record, an "auto-select the first item" effect, a reset)
 *   never calls it. Work that must follow a value set from code belongs where
 *   the code sets it, not in the handler.
 * - `label` follows the label placement setting (on the outline by default,
 *   or above / beside the field), names the input, and is never repeated as a
 *   placeholder.
 */
export interface AppSelectBoxProps<T = unknown> {
  /** The items. */
  dataSource: T[];
  /** Property name used for display text */
  displayExpr: string;
  /** Property name used as the value (key) */
  valueExpr: string;
  /** Current selected value. A value no item matches shows as empty. */
  value: unknown;
  /** Called when the USER picks or clears a value (never when `value` changes from code). */
  onValueChanged: (value: unknown) => void;
  /** Visible label, placed by the label placement setting. Pass it only when this
   *  component owns the field's visible label. When the caller already
   *  renders one (a `<label className="form-label">`, a section header),
   *  use `ariaLabel` instead; two copies of the same text on one field is a
   *  duplicate accessible name, not a second label. */
  label?: string;
  /** Accessible name for the input when the visible label lives outside this
   *  component. Supplies aria-label only: no visible label and no
   *  placeholder. Pass `placeholder` when the empty state needs a hint.
   *  Ignored when `label` is set. */
  ariaLabel?: string;
  /** Hint shown inside the empty field. Never defaults to the label. */
  placeholder?: string;
  /** Shown 4px under the field and tied to it with aria-describedby: a note
   *  about the choice ("No supplier items are linked to this ingredient yet"). */
  helperText?: ReactNode;
  /** Max height of the dropdown list in px (default 300) */
  maxDropdownHeight?: number;
  /** Height of the input; omit to use the theme-responsive --input-height. */
  height?: number;
  /** Width of the input in px (default: fills the container) */
  width?: number;
  /** Show clear button (default true) */
  showClearButton?: boolean;
  /** Disable the control */
  disabled?: boolean;
  /** Required marker on the label and aria-required on the input. */
  required?: boolean;
  /** Where the label goes for this box only. Default: the app-wide setting. */
  labelPlacement?: LabelPlacement;
  /** Whether typing to filter is enabled (default true) */
  searchEnabled?: boolean;
  /** Fields searched; defaults to displayExpr */
  searchExpr?: string | string[];
  /** Read-only mode */
  readOnly?: boolean;
  /** Custom CSS class (on the field wrapper) */
  className?: string;
  /** false marks the field invalid (red outline, aria-invalid). Default true. */
  isValid?: boolean;
  /** Extra attributes for the underlying `<input>`. Pass an `id` here when
   *  the caller renders its own `<label htmlFor>` instead of using `label`
   *  (SettingsDropdown does). An `aria-label` here is used as the
   *  accessible name when neither `label` nor `ariaLabel` is set. */
  inputAttr?: Record<string, unknown>;
  /** Custom dropdown-item renderer. Replaces the default search-highlight
   *  text with the caller's node (e.g. the "Sort only" tag on Menu Category
   *  Priority's add list). */
  itemRender?: (data: T) => ReactNode;
  /** Draws the selected item inside the field in place of its plain text
   *  (e.g. a diet as its coloured chip); null keeps the plain text. Only the
   *  node is styled: the field, outline and label stay as every other field.
   *  See SelectField `renderValue`. */
  renderValue?: (data: T) => ReactNode;
  /** For lists in the thousands (recipes, items): render at most 100
   *  matches with a line saying how many more there are. Typing searches the
   *  whole list. */
  paginate?: boolean;
  /** Focus the input and open the list as soon as it mounts (an editor swapped in for a cell). */
  autoOpen?: boolean;
  /** Called after the list closes, picked or not. */
  onPopupClosed?: () => void;
}

export default function AppSelectBox<T = unknown>(props: AppSelectBoxProps<T>) {
  const {
    dataSource, displayExpr, valueExpr, value, onValueChanged, label, ariaLabel, placeholder, helperText, maxDropdownHeight,
    height, width, showClearButton = true, disabled, required, labelPlacement, searchEnabled = true, searchExpr, readOnly, className,
    isValid = true, inputAttr, itemRender, renderValue, paginate, autoOpen, onPopupClosed,
  } = props;

  // An aria-label passed through inputAttr names the field when nothing else does.
  const attrName = typeof inputAttr?.['aria-label'] === 'string' ? (inputAttr['aria-label'] as string) : undefined;
  let attrs = inputAttr;
  if (attrs && 'aria-label' in attrs) {
    const { 'aria-label': _drop, ...others } = attrs;
    void _drop;
    attrs = others;
  }

  return (
    <SelectField<Record<string, unknown>>
      dataSource={dataSource as unknown as Record<string, unknown>[]}
      displayExpr={displayExpr}
      valueExpr={valueExpr}
      value={value}
      onValueChanged={(v) => onValueChanged(v)}
      label={label}
      ariaLabel={label ? undefined : (ariaLabel ?? attrName)}
      placeholder={placeholder}
      helperText={helperText}
      maxDropdownHeight={maxDropdownHeight}
      height={height}
      width={width}
      showClearButton={showClearButton}
      disabled={disabled}
      required={required}
      labelPlacement={labelPlacement}
      searchEnabled={searchEnabled}
      searchExpr={searchExpr}
      readOnly={readOnly}
      className={className}
      isValid={isValid}
      inputAttr={attrs}
      itemRender={itemRender as ((item: Record<string, unknown>) => ReactNode) | undefined}
      renderValue={renderValue as ((item: Record<string, unknown>) => ReactNode) | undefined}
      paginate={paginate}
      autoOpen={autoOpen}
      onPopupClosed={onPopupClosed}
    />
  );
}
